/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { TableProps } from "interfaces";
import {
  HeaderEnum,
  PatientInterface,
  PatientPharmacyInterface,
  PharmacyInterface,
  QueryResultInterface,
} from "medicalsystem-shared";
import { Dispatch, FC, SetStateAction, SyntheticEvent, useEffect, useMemo, useState } from "react";

import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import Box from "components/generals/Box";
import {
  Checkbox,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { AutoCompleteOption } from "types";
import Autocomplete from "components/generals/Autocomplete";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { blue, blueGrey } from "@mui/material/colors";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context/ThemeContext";

/**
 * Extended properties for the PharmacyPatientTable component.
 * 
 * @interface PharmacyPatientTableInterface
 * @extends {TableProps<PharmacyPatientTableInterface>}
 * @property {Partial<PatientInterface>} [patientSelected] - Currently selected patient
 * @property {Function} [setPharmacyRelationList] - Callback to update pharmacy relations
 * @property {Function} [setDefaultPharmacy] - Callback to set patient's default pharmacy
 */
interface PharmacyPatientTableInterface extends TableProps<PharmacyPatientTableInterface> {
  patientSelected?: Partial<PatientInterface>;
  setPharmacyRelationList?: Dispatch<SetStateAction<PatientPharmacyInterface[]>>;
  setDefaultPharmacy?: Dispatch<SetStateAction<Partial<PharmacyInterface>>>;
}

/**
 * Pharmacy patient table component for managing patient pharmacy relationships.
 * Allows adding/removing pharmacies and setting default pharmacy.
 * 
 * Features:
 * - Add pharmacy via autocomplete dropdown
 * - Remove pharmacy with delete icon button
 * - Set default pharmacy with checkbox (radio-style single selection)
 * - Visual indication of main pharmacy (blue checkbox)
 * - Auto-loads available pharmacies and patient relations
 * - Fixed height table (250px) with scrolling
 * - Supports dark mode via theme controller
 * - Sorted pharmacy options alphabetically
 * - Syncs default pharmacy with parent component
 * 
 * Default Pharmacy Logic:
 * - Only one pharmacy can be marked as default
 * - Main pharmacy shown with blue checkbox icon
 * - Unchecking main pharmacy sets default to null
 * - Setting new main pharmacy unsets previous one
 * 
 * Workflow:
 * 1. Load all available pharmacies
 * 2. Load patient's current pharmacy relations
 * 3. Set initial main pharmacy from patient.defaultPharmacy
 * 4. User adds pharmacy via autocomplete
 * 5. User can set one as main pharmacy
 * 6. Parent receives updated relation list and default pharmacy
 * 
 * @param {PharmacyPatientTableInterface} props - Component properties
 * @param {Partial<PatientInterface>} props.patientSelected - Patient to manage pharmacies for
 * @param {Function} props.setPharmacyRelationList - Callback to update pharmacy relations
 * @param {Function} props.setDefaultPharmacy - Callback to set default pharmacy
 * 
 * @returns {JSX.Element} Editable pharmacy table with default selection
 */
const PharmacyPatientTable: FC<PharmacyPatientTableInterface> = ({
  patientSelected,
  setPharmacyRelationList,
  setDefaultPharmacy,
}) => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [pharmacyList, setPharmacyList] = useState<PharmacyInterface[]>([]);
  const [pharmacySelectList, setPharmacySelectList] = useState<PatientPharmacyInterface[]>([]);
  const [mainPharmacy, setMainPharmacy] = useState<Partial<PharmacyInterface>>();
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  useEffect(() => {
    sendRequest(CoreAPI_URLs.PHARMACIES, onGetPharmacyHandler, "GET");
    if (patientSelected) {
      sendRequest(
        CoreAPI_URLs.PATIENT_PHARMACY_RELATION_GENERAL_OPERATION +
          "/all?patientId=" +
          patientSelected.id,
        onGetPharmacyRelationsHandler,
        "GET"
      );
    }
    setMainPharmacy(patientSelected?.defaultPharmacy ? patientSelected?.defaultPharmacy : null);
  }, [patientSelected]);

  useEffect(() => {
    if (setPharmacyRelationList) {
      setPharmacyRelationList(pharmacySelectList);
    }
  }, [pharmacySelectList]);

  const onGetPharmacyHandler = (pharmacysResponse: QueryResultInterface<PharmacyInterface>) => {
    setPharmacyList(pharmacysResponse.results);
  };

  const onGetPharmacyRelationsHandler = (
    relationsList: QueryResultInterface<PatientPharmacyInterface>
  ) => {
    if (relationsList.results.length > 0) {
      setPharmacySelectList(relationsList.results);
    }
  };

  const pharmacyOptions = useMemo<AutoCompleteOption[]>(() => {
    if (pharmacyList.length === 0) {
      return [];
    } else {
      return pharmacyList
        .map((a) => ({
          label: `${a.storeName} - ${a.displayName} , ${a.address}`.toUpperCase(),
          id: a.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }, [pharmacyList]);

  const onSelectPharmacyHandler = async (
    _event: SyntheticEvent<Element, Event>,
    newValue: AutoCompleteOption
  ) => {
    if (newValue) {
      const selectedPharmacy = pharmacyList.find((p) => p.id === newValue.id);
      if (selectedPharmacy) {
        const exists = pharmacySelectList.some(
          (relation) => relation.pharmacy.id === selectedPharmacy.id
        );
        if (!exists) {
          const newRelation: PatientPharmacyInterface = {
            patient: patientSelected || null,
            pharmacy: selectedPharmacy,
          };
          if (patientSelected) {
            const addRelation = await sendRequest(
              CoreAPI_URLs.PATIENT_PHARMACY_RELATION_GENERAL_OPERATION,
              null,
              "POST",
              JSON.stringify(newRelation)
            );
            setPharmacySelectList((prevList) => [...prevList, addRelation.data]);
          } else {
            setPharmacySelectList((prevList) => [...prevList, newRelation]);
          }
        }
      }
    }
  };

  const onDeletePharmacyHandler = async (pharmacyRelation: PatientPharmacyInterface) => {
    const isMainPharmacy = pharmacyRelation.pharmacy.id === mainPharmacy?.id;

    if (isMainPharmacy) {
      setMainPharmacy(null);
      if (setDefaultPharmacy) setDefaultPharmacy(null);
    }
    if (patientSelected?.id) {
      await sendRequest(
        CoreAPI_URLs.PATIENT_PHARMACY_RELATION_GENERAL_OPERATION,
        null,
        "DELETE",
        null,
        [{ header: HeaderEnum.RESOURCE_ID, value: pharmacyRelation.id }]
      );
      if (isMainPharmacy) {
        patientSelected.defaultPharmacy = null;
        const requestData = {
          ...patientSelected,
          accountNumber: patientSelected.accountNumber.toString(),
        };
        sendRequest(
          CoreAPI_URLs.PATIENTS_GENERAL_OPERATIONS,
          null,
          "PUT",
          JSON.stringify(requestData),
          [{ header: HeaderEnum.RESOURCE_ID, value: patientSelected.id }]
        );
      }
    }

    setPharmacySelectList((prevList) =>
      prevList.filter((pharmacy) => pharmacy.pharmacy.id !== pharmacyRelation.pharmacy.id)
    );
  };

  const handleMainRelationChange = async (idlocal) => {
    let changedPharmacyObject = pharmacySelectList.find(
      (relation) => relation.pharmacy.id === idlocal
    );
    if (changedPharmacyObject) {
      setMainPharmacy(changedPharmacyObject.pharmacy);
      if (setDefaultPharmacy) setDefaultPharmacy(changedPharmacyObject.pharmacy);
      if (patientSelected) {
        patientSelected.defaultPharmacy = changedPharmacyObject.pharmacy;
        const requestData = {
          ...patientSelected,
          accountNumber: patientSelected.accountNumber.toString(),
        };
        await sendRequest(
          CoreAPI_URLs.PATIENTS_GENERAL_OPERATIONS,
          null,
          "PUT",
          JSON.stringify(requestData),
          [{ header: HeaderEnum.RESOURCE_ID, value: patientSelected.id }]
        );
      }
    }
  };

  return (
    <Box sx={{ width: "100%", paddingTop: "2rem" }}>
      <Grid container spacing={2}>
        <Grid item xs={12} paddingBottom={"1rem"} id={"cy-pharmacyidFormContainer"}>
          <Autocomplete
            name="pharmacy.id"
            options={pharmacyOptions}
            label={t("pharmacyPatientTable.selectPharmacy")}
            formikOutside={true}
            onChange={(_e, value) => {
              if (value) {
                onSelectPharmacyHandler(_e, value);
              }
            }}
            noOptionsText={t("pharmacyPatientTable.noOptions")}
          />
        </Grid>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead
                sx={{
                  display: "table-header-group",
                  "& .MuiTableCell-head": {
                    color: darkMode ? "#fff" : "#000",
                  },
                }}
              >
                <TableRow>
                  <TableCell align="center">{t("pharmacyPatientTable.mainPharmacy")}</TableCell>
                  <TableCell align="center">{t("pharmacyPatientTable.displayName")}</TableCell>
                  <TableCell align="center">{t("pharmacyPatientTable.ePrescribe")}</TableCell>
                  <TableCell align="center">{t("pharmacyPatientTable.mailOrder")}</TableCell>
                  <TableCell align="center">{t("pharmacyPatientTable.address")}</TableCell>
                  <TableCell align="center">{t("pharmacyPatientTable.remove")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  display: "table-row-group",
                  "& .MuiTableCell-root": {
                    color: darkMode ? "#fff" : "#000",
                  },
                }}
              >
                {pharmacySelectList.map((relation) => (
                  <TableRow key={relation.pharmacy.id}>
                    <TableCell align="center">
                      <Checkbox
                        checked={mainPharmacy?.id === relation.pharmacy.id || false}
                        name={relation.pharmacy.id}
                        onChange={() => {
                          handleMainRelationChange(relation.pharmacy.id);
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">{relation.pharmacy.displayName}</TableCell>
                    <TableCell align="center">
                      {relation.pharmacy.ePrescribe ? (
                        <CheckBoxRoundedIcon fontSize="medium" sx={{ color: blue[400] }} />
                      ) : (
                        <CheckBoxRoundedIcon fontSize="medium" sx={{ color: blueGrey[100] }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {relation.pharmacy.mailOrderPharmacy ? (
                        <CheckBoxRoundedIcon fontSize="medium" sx={{ color: blue[400] }} />
                      ) : (
                        <CheckBoxRoundedIcon fontSize="medium" sx={{ color: blueGrey[100] }} />
                      )}
                    </TableCell>
                    <TableCell align="center">{relation.pharmacy.address}</TableCell>
                    <TableCell align="center">
                      <IconButton type="button" onClick={() => onDeletePharmacyHandler(relation)}>
                        <DeleteRoundedIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PharmacyPatientTable;
